import { Link } from 'react-router-dom';
import { ShoppingBag, Trash2 } from 'lucide-react';
import { useCart } from '@/providers/CartProvider';
import { usePlatform } from '@/providers/PlatformProvider';
import { usePageMetadata } from '@/hooks/usePageMetadata';
import { QuantitySelector } from '@/components/ui/QuantitySelector';
import { Button, ButtonLink } from '@/components/ui/Button';
import { Alert } from '@/components/ui/Alert';
import { EmptyState } from '@/components/system/EmptyState';
import { BrandArtwork } from '@/components/brand/BrandArtwork';
import type { CartItem } from '@/types/cart';
import { PageHeader } from '@/components/layout/PageHeader';

/**
 * Standalone cart page — BUILD-PLAN §10 (page list), §13, §63.
 *
 * The drawer is for a quick glance mid-browse; this is the considered review
 * before checkout, so it has room for personalisation detail, per-line
 * pricing and the full summary. Both read the same server-owned cart, so they
 * can never disagree.
 */
export function CartPage() {
  const { cart, isLoading, isMutating, updateQuantity, remove, clear } = useCart();
  const { isEnabled } = usePlatform();

  usePageMetadata({
    title: 'Your Cart',
    description: 'Review the pieces you have chosen.',
  });

  const items = cart?.items ?? [];

  return (
    <div className="mx-auto max-w-6xl px-5 py-12 sm:px-6 lg:px-8 lg:py-16">
      <PageHeader eyebrow="Your selection" title="Cart" className="mb-10" />

      {isLoading ? (
        <div className="flex flex-col gap-6" role="status" aria-label="Loading your cart">
          {[0, 1].map((index) => (
            <div key={index} className="flex gap-5">
              <div className="h-32 w-24 animate-pulse bg-ink/[0.09] motion-reduce:animate-none" />
              <div className="flex flex-1 flex-col gap-3">
                <div className="h-5 w-1/3 animate-pulse bg-ink/[0.09] motion-reduce:animate-none" />
                <div className="h-4 w-1/4 animate-pulse bg-ink/[0.09] motion-reduce:animate-none" />
              </div>
            </div>
          ))}
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={ShoppingBag}
          title="Your cart is empty"
          description="When you find something you love, it will appear here."
          action={<ButtonLink to="/shop">Explore the Collection</ButtonLink>}
        />
      ) : (
        <div className="grid gap-12 lg:grid-cols-[1fr_22rem] lg:gap-16">
          <section aria-label="Cart items" className="flex flex-col">
            {items.map((item) => (
              <CartRow
                key={item.id}
                item={item}
                disabled={isMutating}
                onQuantity={(quantity) => void updateQuantity(item.id, quantity)}
                onRemove={() => void remove(item.id)}
              />
            ))}

            <div className="mt-8">
              <Button variant="ghost" size="sm" onClick={() => void clear()} disabled={isMutating}>
                Empty cart
              </Button>
            </div>
          </section>

          {/* Summary is sticky on desktop so the total stays in view while a
              long list is reviewed. */}
          <aside className="lg:sticky lg:top-24 lg:self-start">
            <div className="border border-line bg-surface p-7">
              {/* The site's rule-and-label mark, so the summary panel belongs to the
                  same system as every other section heading. */}
              <div className="flex items-center gap-3">
                <span className="h-px w-6 bg-accent/50" aria-hidden="true" />
                <h2 className="font-display text-xl text-ink">Summary</h2>
              </div>

              <dl className="mt-6 flex flex-col gap-3 text-sm">
                <div className="flex justify-between">
                  <dt className="text-muted">Subtotal</dt>
                  <dd className="text-ink">{cart?.totals.subtotal.formatted}</dd>
                </div>

                {cart && cart.totals.personalisation.minor > 0 && (
                  <div className="flex justify-between">
                    <dt className="text-muted">Personalisation</dt>
                    <dd className="text-ink">{cart.totals.personalisation.formatted}</dd>
                  </div>
                )}
              </dl>

              {/*
                Shipping and tax are described, not priced. §86: showing
                "£0.00" would read as free delivery, which we cannot promise
                until the delivery zone is known at checkout.
              */}
              <p className="mt-5 border-t border-line pt-5 text-xs leading-relaxed text-muted">
                Shipping and any applicable taxes are calculated at checkout, once a
                delivery address is known.
              </p>

              <div className="mt-7">
                {isEnabled('checkout') ? (
                  <ButtonLink to="/checkout" size="lg" fullWidth>
                    Proceed to Checkout
                  </ButtonLink>
                ) : (
                  <Alert tone="info">
                    Online checkout opens soon.{' '}
                    <Link to="/contact" className="underline underline-offset-4">
                      Enquire about these pieces
                    </Link>
                  </Alert>
                )}
              </div>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}

function CartRow({
  item,
  disabled,
  onQuantity,
  onRemove,
}: {
  item: CartItem;
  disabled: boolean;
  onQuantity: (quantity: number) => void;
  onRemove: () => void;
}) {
  const personalisation = Object.entries(item.personalisation ?? {});

  return (
    <article className="flex gap-5 border-b border-line py-7 first:pt-0">
      <Link
        to={`/products/${item.product.slug}`}
        className="h-32 w-24 shrink-0 overflow-hidden bg-surface-muted"
      >
        {item.product.image ? (
          <img
            src={item.product.image}
            alt={item.product.name}
            loading="lazy"
            className="h-full w-full object-cover"
          />
        ) : (
          <BrandArtwork seed={item.product.name} ratio="portrait" />
        )}
      </Link>

      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <Link
              to={`/products/${item.product.slug}`}
              className="font-display text-lg leading-snug text-ink hover:text-accent"
            >
              {item.product.name}
            </Link>

            {item.variant.name && <p className="mt-1 text-sm text-muted">{item.variant.name}</p>}

            <p className="mt-1 text-sm text-muted">{item.unitPrice.formatted} each</p>
          </div>

          <button
            type="button"
            onClick={onRemove}
            disabled={disabled}
            aria-label={`Remove ${item.product.name} from cart`}
            className="flex h-10 w-10 shrink-0 items-center justify-center text-muted transition-colors hover:text-danger disabled:opacity-40"
          >
            <Trash2 className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" />
          </button>
        </div>

        {personalisation.length > 0 && (
          <dl className="mt-3 flex flex-col gap-1 border-l border-accent/40 pl-3 text-xs text-muted">
            {personalisation.map(([key, value]) => (
              <div key={key} className="flex gap-1.5">
                <dt className="capitalize">{key.replace(/_/g, ' ')}:</dt>
                <dd className="text-ink">{value}</dd>
              </div>
            ))}
          </dl>
        )}

        {item.priceChanged && (
          <p className="mt-3 text-xs text-warning">
            The price of this piece has changed since you added it.
          </p>
        )}

        {item.exceedsStock && (
          <p className="mt-3 text-xs text-danger">
            Only {item.availableStock} remain — please reduce the quantity.
          </p>
        )}

        <div className="mt-auto flex items-center justify-between gap-4 pt-5">
          <QuantitySelector
            value={item.quantity}
            onChange={onQuantity}
            min={0}
            max={item.availableStock ?? 50}
            disabled={disabled}
          />

          <span className="font-display text-lg text-ink">{item.lineTotal.formatted}</span>
        </div>
      </div>
    </article>
  );
}
